import i18n from '../config/i18n';
import { authenticatedFetch } from './authService';

export type SessionFilter = 'total' | 'pendentes' | 'erros';

export interface FlashcardSummary {
  articleCount: number;
  total: number;
  acertados: number;
  erros: number;
  pendentes: number;
}

export interface Flashcard {
  id: string;
  articleId: string;
  articleTitle?: string;
  subTopicId: number;
  subTopicName?: string;
  frontText: string;
  frontImageUrl: string | null;
  backText: string;
  backImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function getFlashcardSummary(): Promise<FlashcardSummary> {
  try {
    const response = await authenticatedFetch('/flashcards/summary', {
      method: 'GET',
    });
    if (!response.ok) {
      const err = await safeJson(response);
      throw new Error(err?.message || i18n.t('flashcards.errors.fetchSummary'));
    }
    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('flashcards.errors.fetchSummary'));
  }
}

export async function getFlashcardSession(params: {
  filter: SessionFilter;
  limit: number;
}): Promise<Flashcard[]> {
  try {
    const search = new URLSearchParams({
      filter: params.filter,
      limit: String(params.limit),
    });
    const response = await authenticatedFetch(
      `/flashcards/session?${search.toString()}`,
      { method: 'GET' },
    );
    if (!response.ok) {
      const err = await safeJson(response);
      throw new Error(err?.message || i18n.t('flashcards.errors.fetchSession'));
    }
    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('flashcards.errors.fetchSession'));
  }
}

export async function getArticleFlashcards(
  articleId: string,
): Promise<Flashcard[]> {
  try {
    const response = await authenticatedFetch(
      `/articles/${encodeURIComponent(articleId)}/flashcards`,
      { method: 'GET' },
    );
    if (!response.ok) {
      const err = await safeJson(response);
      throw new Error(
        err?.message || i18n.t('flashcards.errors.fetchArticleFlashcards'),
      );
    }
    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('flashcards.errors.fetchArticleFlashcards'));
  }
}

export async function submitFlashcardAttempt(
  flashcardId: string,
  isCorrect: boolean,
): Promise<void> {
  try {
    const response = await authenticatedFetch(
      `/flashcards/${encodeURIComponent(flashcardId)}/attempts`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isCorrect }),
      },
    );
    if (!response.ok) {
      const err = await safeJson(response);
      throw new Error(err?.message || i18n.t('flashcards.errors.submitAttempt'));
    }
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('flashcards.errors.submitAttempt'));
  }
}

async function safeJson(response: Response): Promise<any | null> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
